import { useIntl } from "react-intl";
import { LabelValueItem } from "@/model";
import { ParagraphDataType, ParagraphType } from "@/model";
import { getLabelFromValue } from "@/util/typeUtil";
import { NestedDropdown, NestedDropdownOption } from "@/component/input";
import { DirectoryIcon } from '@/assets/svgs/icons';


type ParagraphTypeLabel =
    "Output" | "Utility" | "Input" | "Next" | "Template";
type ParagraphDataTypeLabel =
    "Text" | "Carousel" | "Media" | "File" | "Custom" | "LLM Answer" | "RAW" | "Variable" | "Python Code" | "API" | "List" | "Quick Reply";

const DROPDOWN_OPTIONS: NestedDropdownOption<ParagraphTypeLabel, ParagraphDataTypeLabel>[] = [
    { primary: "Output", secondary: [ "Text", "Carousel", "Media", "File", "Custom", "LLM Answer", "RAW" ] },
    { primary: "Utility", secondary: ["Variable", "Python Code", "API"] },
    { primary: "Input", secondary: [ "List", "Quick Reply", "Text" ] },
    { primary: "Next" },
    {
        primary: "Template",
        children: (
            <span className="flex items-center gap-1">
                <DirectoryIcon className="size-4" />
                {"Template"} 
            </span>
        )
    }
];

const PARAGRAPH_TYPE_LABELS: LabelValueItem<ParagraphType>[] = [
    { label: "Output", value: "output" },
    { label: "Utility", value: "utility" },
    { label: "Input", value: "input" },
    { label: "Next", value: "next" },
    { label: "Template", value: "template" }
];

const PARAGRAPH_DATA_TYPE_LABELS: LabelValueItem<ParagraphDataType>[] = [
    { label: "Text", value: "text" },
    { label: "Carousel", value: "carousel" },
    { label: "Media", value: "media" },
    { label: "File", value: "file" },
    { label: "Custom", value: "custom" },
    { label: "LLM Answer", value: "llm_answer" },
    { label: "RAW", value: "raw" },
    { label: "Variable", value: "variable" },
    { label: "Python Code", value: "code" },
    { label: "API", value: "api" },
    { label: "List", value: "list" },
    { label: "Quick Reply", value: "quick_reply" },
];

const getIndexFromLabels = (
    type: ParagraphType, dataType: ParagraphDataType
): {primary: number, secondary?: number} => {
    const priamryLabel = getLabelFromValue(PARAGRAPH_TYPE_LABELS, type);
    const priamryIndex = DROPDOWN_OPTIONS.findIndex(option => option.primary === priamryLabel);
    const secondaryLabel = getLabelFromValue(PARAGRAPH_DATA_TYPE_LABELS, dataType);
    const secondaryIndex = DROPDOWN_OPTIONS[priamryIndex].secondary?.findIndex(option => option === secondaryLabel);
    return {
        primary: priamryIndex,
        secondary: secondaryIndex !== -1 ? secondaryIndex : undefined
    };
};

const getParagraphTypesFromLabels = (
    type: ParagraphTypeLabel, dataType?: ParagraphDataTypeLabel
): {type: ParagraphType, dataType: ParagraphDataType} => {
    if (type === "Next") {
        return { type: "next", dataType: "next" };
    } else if (type === "Template") {
        return { type: "template", dataType: "template" };
    } else {
        return {
            type: PARAGRAPH_TYPE_LABELS.find(({ label }) => label === type)!.value,
            dataType: PARAGRAPH_DATA_TYPE_LABELS.find(({ label }) => label === dataType)!.value
        };
    }
};

interface ParagraphTypeDropdownProps {
    type: ParagraphType;
    dataType: ParagraphDataType;
    onChange: (type: ParagraphType, dataType: ParagraphDataType) => void;
}

const ParagraphTypeDropdown: React.FC<ParagraphTypeDropdownProps> = (props) => {
    const { type, dataType, onChange } = props;
    const intl = useIntl();

    return (
        <NestedDropdown width={280}
                        options={DROPDOWN_OPTIONS}
                        selectedKeys={getIndexFromLabels(type, dataType)}
                        data-testid="paragraph-card-type-dropdown"
                        aria-label={intl.formatMessage({ id: "i04006" })}
                        onSelect={(primary, secondary) => {
                            const { type, dataType } = getParagraphTypesFromLabels(
                                DROPDOWN_OPTIONS[primary].primary,
                                DROPDOWN_OPTIONS[primary].secondary?.[secondary!]
                            );
                            onChange(type, dataType);
                        }}
        />
    );
};

export default ParagraphTypeDropdown;
